import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import CloseIcon from '@mui/icons-material/Close';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { BiCloudDownload } from 'react-icons/bi';
import { useDispatch } from 'react-redux';
import {
  Box,
  Toolbar,
  Tooltip,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Collapse,
  Stack
} from '@mui/material';
import { getCityData } from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

export default function NetworkToolbar({
  numSelected,
  filterName,
  onFilterName,
  parent,
  pageMenu,
  setFilterState,
  filterState,
  setFilterName,
  handleDownload
}) {
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const { CityDrop, LoanTypeDrop } = useGlobalSelector();
  const dispatch = useDispatch();

  const handleGetState = (optionVal) => {
    dispatch(getCityData({ stateId: optionVal }));
  };

  useEffect(() => {
    handleGetState(null);
  }, []);

  return (
    <Box sx={{ width: '100%', borderBottom: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
      <Toolbar
        sx={{
          minHeight: '68px !important',
          px: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}
      >
        <OutlinedInput
          value={filterName}
          onChange={onFilterName}
          placeholder="Search network..."
          startAdornment={
            <InputAdornment position="start">
              <Box
                component={Icon}
                icon={searchFill}
                sx={{ color: '#94a3b8', width: 20, height: 20 }}
              />
            </InputAdornment>
          }
          sx={{
            width: { xs: '100%', sm: 320 },
            height: 42,
            borderRadius: '10px',
            bgcolor: '#f8fafc',
            fontSize: 14,
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: '#13519d' }
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Filter list">
            <IconButton
              onClick={() => setIsOpenFilter((prev) => !prev)}
              sx={{
                color: isOpenFilter ? '#13519d' : '#64748b',
                backgroundColor: isOpenFilter ? 'rgba(19, 81, 157, 0.08)' : '#f8fafc',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                p: 1,
                '&:hover': { backgroundColor: 'rgba(19, 81, 157, 0.12)' }
              }}
            >
              <Icon icon={roundFilterList} width={20} height={20} />
            </IconButton>
          </Tooltip>

          {handleDownload && (
            <Tooltip title="Download Data" placement="top" arrow>
              <IconButton
                onClick={handleDownload}
                sx={{
                  color: '#13519d',
                  backgroundColor: 'rgba(19, 81, 157, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  p: 1,
                  '&:hover': { backgroundColor: 'rgba(19, 81, 157, 0.16)' }
                }}
              >
                <BiCloudDownload size={20} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Toolbar>

      <Collapse in={isOpenFilter}>
        <Box sx={{ px: 3, py: 2, bgcolor: '#fafbfc', borderTop: '1px solid #f1f5f9' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3.5}>
              <Select
                fullWidth
                size="small"
                value={filterState.typeId ?? ' '}
                onChange={(e) => {
                  const v = e.target.value === ' ' ? null : e.target.value;
                  setFilterState((prev) => ({
                    ...prev,
                    typeId: v
                  }));
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" ">
                  <em>All Products</em>
                </MenuItem>
                {LoanTypeDrop?.map((i) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={4} md={3.5}>
              <Autocomplete
                disablePortal
                id="combo-box-city"
                size="small"
                options={CityDrop || []}
                getOptionLabel={(option) => option?.label ?? ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={CityDrop?.find((opt) => opt.value === filterState.cityId) ?? null}
                onChange={(e, newValue) => {
                  setFilterState((prev) => ({
                    ...prev,
                    cityId: newValue ? newValue.value : null
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select City"
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' }
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={3.5}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={filterState.isActive ?? ''}
                renderValue={(value) => {
                  if (value === '' || value === null) {
                    return <span style={{ color: '#94a3b8' }}>All Statuses</span>;
                  }
                  return value === 'true' ? 'Active' : 'Inactive';
                }}
                onChange={(e) => {
                  setFilterState((prev) => ({
                    ...prev,
                    isActive: e.target.value === '' ? null : e.target.value
                  }));
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm="auto">
              <Tooltip title="Reset Filters">
                <IconButton
                  onClick={() => {
                    setFilterState({
                      cityId: null,
                      typeId: null,
                      isActive: null
                    });
                    setFilterName('');
                  }}
                  sx={{
                    color: '#dc2626',
                    bgcolor: '#fee2e2',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
